import { useEffect, useState, type RefCallback } from 'react';

type ObserverPool = {
  observer: IntersectionObserver;
  listeners: Map<Element, () => void>;
};

const pools = new Map<string, ObserverPool>();

/** Share browser-scheduled visibility measurements between deferred sections. */
function observeOnce(element: Element, rootMargin: string, onVisible: () => void) {
  let pool = pools.get(rootMargin);
  if (!pool) {
    const listeners = new Map<Element, () => void>();
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        listeners.get(entry.target)?.();
      }
    }, { rootMargin });
    pool = { observer, listeners };
    pools.set(rootMargin, pool);
  }
  const activePool = pool;
  let active = true;
  const stop = () => {
    if (!active) return;
    active = false;
    activePool.listeners.delete(element);
    activePool.observer.unobserve(element);
    if (activePool.listeners.size === 0) {
      activePool.observer.disconnect();
      if (pools.get(rootMargin) === activePool) pools.delete(rootMargin);
    }
  };
  activePool.listeners.set(element, () => {
    stop();
    onVisible();
  });
  activePool.observer.observe(element);
  return stop;
}

/**
 * Deferred content has a stable placeholder, so it can wait for the browser's
 * first observer notification. No synchronous layout read is needed at mount.
 * Observation also covers restored scroll positions and later viewport changes.
 */
export function useDeferredInView<T extends Element = HTMLDivElement>(
  rootMargin: string,
): { ref: RefCallback<T>; inView: boolean } {
  const [element, setElement] = useState<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!element || inView) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    return observeOnce(element, rootMargin, () => setInView(true));
  }, [element, rootMargin, inView]);

  return { ref: setElement, inView };
}
